import { useState } from 'react';

/**
 * Mapeo de los campos que trajo el formulario contra los del CRM.
 *
 * Muestra siempre el valor real que llegó junto a cada clave: las etiquetas de
 * Elementor son preguntas enteras ("¿A qué sector pertenece tu empresa?") y sin
 * ver el dato es fácil elegir el destino equivocado.
 */
export default function FieldMapper({
    detected,
    fieldMap,
    targets,
    customFields,
    onChange,
    onCreateCustomField,
}) {
    const [creatingFor, setCreatingFor] = useState(null);
    const [newFieldName, setNewFieldName] = useState('');

    const setTarget = (path, target) => {
        const next = { ...fieldMap };
        if (!target) delete next[path];
        else next[path] = target;
        onChange(next);
    };

    const startCreating = (row) => {
        setCreatingFor(row.path);
        // La etiqueta del formulario suele ser una pregunta; se ofrece como
        // punto de partida y el usuario la acorta a un nombre de campo.
        setNewFieldName(row.path.replace(/[_¿?]/g, ' ').replace(/\s+/g, ' ').trim());
    };

    const confirmCreate = async () => {
        if (!newFieldName.trim()) return;
        await onCreateCustomField(creatingFor, newFieldName.trim());
        setCreatingFor(null);
        setNewFieldName('');
    };

    if (detected.length === 0) {
        return (
            <div className="text-center py-10 px-6 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl">
                <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-700">inbox</span>
                <h3 className="mt-2 text-sm font-semibold dark:text-white">Todavía no llegó ningún envío</h3>
                <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
                    Pegá la URL en el webhook del formulario y enviálo una vez. Ese primer envío no
                    crea el lead: sirve para que acá aparezcan los campos reales y puedas mapearlos.
                </p>
            </div>
        );
    }

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-900/40 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <div className="col-span-7">Campo recibido</div>
                <div className="col-span-5">Guardar en</div>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {detected.map((row) => {
                    const target = fieldMap[row.path] || '';
                    const isCreating = creatingFor === row.path;

                    return (
                        <div key={row.path} className="grid grid-cols-12 gap-2 px-4 py-3 items-center">
                            <div className="col-span-7 min-w-0">
                                <p className="text-sm font-medium dark:text-white truncate" title={row.path}>
                                    {row.path.replace(/_/g, ' ')}
                                </p>
                                <p className="text-xs text-gray-400 truncate" title={row.sample}>
                                    {row.sample || <span className="italic">vino vacío</span>}
                                </p>
                            </div>

                            <div className="col-span-5">
                                {isCreating ? (
                                    <div className="flex items-center gap-1">
                                        <input
                                            autoFocus
                                            value={newFieldName}
                                            onChange={(e) => setNewFieldName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') confirmCreate();
                                                if (e.key === 'Escape') setCreatingFor(null);
                                            }}
                                            placeholder="Nombre del campo"
                                            className="flex-1 min-w-0 text-xs p-2 border border-primary rounded-lg dark:bg-slate-800 dark:text-white"
                                        />
                                        <button onClick={confirmCreate} title="Crear campo"
                                            className="shrink-0 text-emerald-600 hover:text-emerald-500">
                                            <span className="material-symbols-outlined text-lg">check</span>
                                        </button>
                                        <button onClick={() => setCreatingFor(null)} title="Cancelar"
                                            className="shrink-0 text-gray-400 hover:text-red-500">
                                            <span className="material-symbols-outlined text-lg">close</span>
                                        </button>
                                    </div>
                                ) : (
                                    <select
                                        value={target}
                                        onChange={(e) => {
                                            if (e.target.value === '__new__') startCreating(row);
                                            else setTarget(row.path, e.target.value);
                                        }}
                                        className={`w-full text-xs p-2 border rounded-lg dark:bg-slate-800 dark:text-white ${
                                            target
                                                ? 'border-gray-200 dark:border-gray-700'
                                                : 'border-dashed border-gray-300 dark:border-gray-600 text-gray-400'
                                        }`}
                                    >
                                        <option value="">No guardar</option>
                                        <optgroup label="Campos del contacto">
                                            {Object.entries(targets).map(([key, label]) => (
                                                <option key={key} value={key}>{label}</option>
                                            ))}
                                        </optgroup>
                                        {customFields.length > 0 && (
                                            <optgroup label="Campos personalizados">
                                                {customFields.map((f) => (
                                                    <option key={f.key} value={`custom:${f.key}`}>{f.name}</option>
                                                ))}
                                            </optgroup>
                                        )}
                                        <option value="__new__">+ Crear campo personalizado…</option>
                                    </select>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <p className="px-4 py-2 text-[11px] text-gray-400 bg-gray-50 dark:bg-gray-900/40">
                Lo que dejes en «No guardar» igual queda registrado en el envío, así que se puede
                recuperar más adelante sin volver a pedirle el formulario al cliente.
            </p>
        </div>
    );
}
